import Seo from './Seo'
import {relatedMarketingPages} from './seoConstants'
import {softwareApplicationSchema, faqSchema} from './schema'
import {CtaSection, FaqSection, InlineText, RelatedLinks, ScreenshotGrid} from './seoSections'
import type {ComparisonData} from './seoTypes'
import './SeoPage.css'

// Reusable comparison / "alternative" landing page, driven entirely by a
// ComparisonData object. Renders unique SEO head + SoftwareApplication & FAQPage
// JSON-LD, a fair feature comparison table, and CTAs into the free editor.
const ComparisonPage = ({data}: {data: ComparisonData}) => {
  const related = relatedMarketingPages(data.canonicalPath)

  return (
    <div className="seo-root" data-prerender-ready>
      <Seo
        title={data.title}
        description={data.description}
        canonicalPath={data.canonicalPath}
        ogImage={data.ogImage}
        jsonLd={[softwareApplicationSchema(), faqSchema(data.faq)]}
      />
      <div className="seo-grid" aria-hidden />

      <main className="seo-page">
        <header className="seo-hero">
          <span className="seo-eyebrow">
            <span className="seo-eyebrow-dot" />
            {data.eyebrow}
          </span>
          <h1 className="seo-h1">{data.h1}</h1>
          <p className="seo-lede">{data.heroLede}</p>
        </header>

        <section className="seo-section">
          {data.intro.map((para, i) => (
            <p key={i} className="seo-p">
              <InlineText text={para} />
            </p>
          ))}
        </section>

        <section className="seo-section seo-card">
          <h2 className="seo-h2">{data.whatTheyreGoodAtTitle}</h2>
          <ul className="seo-list">
            {data.whatTheyreGoodAt.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">{data.whyEcranifyTitle}</h2>
          <ul className="seo-list">
            {data.whyEcranify.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">{data.comparisonTitle}</h2>
          <div className="seo-table-wrap">
            <table className="seo-table">
              <caption>Ecranify vs {data.competitor} — feature comparison</caption>
              <thead>
                <tr>
                  <th scope="col" className="seo-col-feature">
                    Feature
                  </th>
                  <th scope="col" className="seo-col-ecranify">
                    Ecranify
                  </th>
                  <th scope="col" className="seo-col-competitor">
                    {data.competitor}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.comparisonRows.map((row) => (
                  <tr key={row.feature}>
                    <td className="seo-col-feature">{row.feature}</td>
                    <td className="seo-col-ecranify">{row.ecranify}</td>
                    <td className="seo-col-competitor">{row.competitor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">See Ecranify in action</h2>
          <ScreenshotGrid shots={data.screenshots} variant="contain" />
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">{data.whoShouldTitle}</h2>
          <ul className="seo-list">
            {data.whoShould.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <FaqSection title="Frequently asked questions" faq={data.faq} />

        <RelatedLinks title="Keep exploring" links={related} />

        <CtaSection cta={data.cta} />
      </main>
    </div>
  )
}

export default ComparisonPage
